import React from 'react'
import { NavLink } from 'react-router-dom'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { logAmplitudeEvent } from '../../../amplitude/amplitude'
import { ACTIVITY, INCOMING_ORDERS, INVENTORY, INVENTORY_DASHBOARD, OUTGOING_ORDERS } from '../HardwareMenu'
import MenuItemLinkText from './MenuItemLinkText'

const useStyles = makeOpenSolarStyles(
  (theme) => ({
    wrapper: {
      boxSizing: 'border-box',
      padding: '0px 5px',
      width: '100%',
      alignItems: 'center',
      display: 'inline-flex',
      '&:hover': {
        background: '#F5F5F5',
      },
    },
    arrow: {
      cursor: 'pointer',
      flexBasis: 1,
    },
    navLink: {
      margin: 0,
      flexGrow: 1,
      minWidth: 0,
      padding: '10px 0px', // extra padding allow user to easy click
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'flex-start',
    },
    linkContent: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '4px 5px',
      minWidth: 0,
      flex: 1,
    },
    warningIcon: {
      marginLeft: '5px',
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      flexShrink: 0,
    },
    chip: {
      flexShrink: 0,
    },
  }),
  { name: 'OSMenuItemLink' }
)

export type MenuItemLinkPropsType = {
  title: string
  url: string
  id: string
  ArrowIconNode?: React.ReactNode
  warningIcon?: React.ReactNode
  onClick?: () => void
  exact?: boolean

  // Some menu items have no url, and so don't have an internal way of knowing whether they're 'selected/open'
  // This flag lets the parent component pass in whether the menu item is open or not
  open?: boolean
  chip?: React.ReactNode
  // Opens the link in a new tab (for external URLs) if set to true
  externalLink?: boolean
  highlightText?: string
}

const MenuItemLink = ({
  title,
  url,
  id,
  ArrowIconNode,
  warningIcon,
  onClick,
  exact = false,
  open,
  chip,
  externalLink = false,
  highlightText,
}: MenuItemLinkPropsType) => {
  const classes = useStyles()

  const logEvent = () => {
    switch (title) {
      case INVENTORY_DASHBOARD:
        logAmplitudeEvent('generic_button_clicked', {
          source: title,
          context: 'inventory_LHS_nav',
        })
        break
      case INVENTORY:
        logAmplitudeEvent('generic_button_clicked', {
          source: title,
          context: 'inventory_LHS_nav',
        })
        break
      case INCOMING_ORDERS:
        logAmplitudeEvent('generic_button_clicked', {
          source: title,
          context: 'inventory_LHS_nav',
        })
        break
      case OUTGOING_ORDERS:
        logAmplitudeEvent('generic_button_clicked', {
          source: title,
          context: 'inventory_LHS_nav',
        })
        break
      case ACTIVITY:
        logAmplitudeEvent('generic_button_clicked', {
          source: title,
          context: 'inventory_LHS_nav',
        })
        break
      case 'Auto Design Templates':
        logAmplitudeEvent('auto_design_templates_control_lhs_nav_clicked', {
          source: 'Auto Design Templates Control',
          context: 'design_and_hardware_LHS_nav',
          eventName: 'Navigation Auto Design Templates Clicked',
          description: 'User clicked Auto Design Templates from main navigation menu',
          destination: '/system_templates',
          section: 'Design and Hardware',
        })
        break
      default:
        break
    }
  }

  const linkContent = (
    <div className={classes.linkContent}>
      <MenuItemLinkText label={title} highlightText={highlightText} />
      {warningIcon && <span className={classes.warningIcon}>{warningIcon}</span>}
      {chip && <span className={classes.chip}>{chip}</span>}
    </div>
  )

  const renderLink = () => {
    if (!url) {
      return (
        <div className={classes.navLink} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
          <div className={classes.linkContent}>
            <MenuItemLinkText label={title} style={{ fontWeight: !open ? 'normal' : 'bold' }} highlightText={highlightText} />
            {warningIcon && <span className={classes.warningIcon}>{warningIcon}</span>}
            {chip && <span className={classes.chip}>{chip}</span>}
          </div>
        </div>
      )
    }

    if (externalLink) {
      return (
        <a
          key={title}
          href={url}
          id={id}
          className={classes.navLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            logEvent()
            onClick && onClick()
          }}
        >
          {linkContent}
        </a>
      )
    }

    return (
      <NavLink
        key={title}
        to={url}
        id={id}
        activeStyle={{
          fontWeight: 'bold',
        }}
        className={classes.navLink}
        onClick={() => {
          logEvent()
          onClick && onClick()
        }}
        exact={exact}
      >
        {linkContent}
      </NavLink>
    )
  }

  return (
    <div className={classes.wrapper}>
      {renderLink()}
      {!!ArrowIconNode && <span className={classes.arrow}>{ArrowIconNode}</span>}
    </div>
  )
}

export default MenuItemLink
